import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

import Particle from "../Particle";
import Testimonials from "./Testimonials";
import reviews from "./data";
import useInViewOnce from "../../hooks/useInViewOnce";

import seniorsTraining from "../../Assets/seniors_training.jpg";
import seniors1 from "../../Assets/senior1.jpg";
import seniors2 from "../../Assets/seniors2.jpg";
import seniors4 from "../../Assets/seniors4.jpg";
import seniors3 from "../../Assets/seniors3.png";
import seniors5 from "../../Assets/seniors5.jpg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

const sliderImages = [
  { src: seniors1, alt: "Seniors digital literacy session" },
  { src: seniors2, alt: "Hands-on technology training" },
  { src: seniors4, alt: "Community learning for seniors" },
  { src: seniors3, alt: "Digital skills in action" },
  { src: seniors5, alt: "Connected learning moments" },
];

const skillsTaught = [
  "Troubleshooting common device issues",
  "Cybersecurity and safe online practices",
  "Accessing online services, telehealth, and educational resources",
  "Using AI (Artificial Intelligence) responsibly",
  "Staying connected through email and video calls",
  "Collaborative tech projects with youth",
];

const whyItMatters = [
  "Maintain independence and confidence in daily life",
  "Stay socially connected within community",
  "Engage in lifelong learning, hobbies, and personal development",
];

const approach = [
  {
    title: "Neighborhood-Based Learning",
    text: "Programs are hosted at community centers, senior centers, and apartment complexes to reduce transportation barriers.",
  },
  {
    title: "Hands-On, Accessible Instruction",
    text: "Interactive lessons are paced for all levels so participants build confidence through real-world practice.",
  },
  {
    title: "Lifelong Learning & Cognitive Health",
    text: "Activities encourage creativity, personal growth, and cognitive stimulation through exploration of digital tools.",
  },
  {
    title: "Peer & Volunteer Support",
    text: "Patient guidance and small group sessions help seniors feel supported, empowered, and connected.",
  },
  {
    title: "Intergenerational Learning",
    text: "Seniors share wisdom with younger generations while learning new digital skills—strengthening community bonds.",
  },
  {
    title: "Customized & Adaptive Learning",
    text: "Lessons are tailored to skill levels and accessibility needs, accommodating physical or cognitive considerations.",
  },
  {
    title: "Safe & Supportive Environment",
    text: "A welcoming, non-judgmental setting where seniors can explore, ask questions, and practice without fear.",
  },
];

function Senior() {
  const navigate = useNavigate();

  const handleRegister = () => {
    navigate("/registration#seniors-section");
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2800,
    pauseOnHover: true,
    pauseOnFocus: true,
    adaptiveHeight: true,
  };

  const videoId = "5mPU-mIQgoQ";

  const { ref: topRef, hasEntered: topSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: overviewRef, hasEntered: overviewSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: whyRef, hasEntered: whySeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: approachRef, hasEntered: approachSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: testimonialsRef, hasEntered: testimonialsSeen } = useInViewOnce({ threshold: 0.18, once: true });

  return (
    <Container fluid className="about-section seniors-page">
      <Particle />

      <Container className="seniors-container">
        <header className="seniors-hero">
          <h1 className="seniors-hero-title">
            Empowering <strong className="purple">Seniors</strong> with Technology
          </h1>
          <p className="seniors-hero-lead">
            Digital literacy support that builds confidence, independence, and connection.
          </p>
          <div className="seniors-hero-cta">
            <Button onClick={handleRegister} className="custom-yellow-btn seniors-cta-btn">
              Register Now
            </Button>
          </div>
        </header>

        <div ref={topRef} className={`seniors-reveal${topSeen ? " is-seen" : ""}`}>
          <Row className="seniors-top" style={{ justifyContent: "center" }}>
            <Col md={7} className="seniors-left">
              <div className="policy-card seniors-slider-card" aria-label="Seniors program highlights">
                <Slider {...settings}>
                  {sliderImages.map((image, index) => (
                    <div key={index} className="seniors-slide">
                      <img src={image.src} alt={image.alt} className="seniors-slide-img" />
                    </div>
                  ))}
                </Slider>
              </div>
            </Col>

            <Col md={5} className="seniors-right">
              <div className="policy-card seniors-summary-card">
                <h2 className="seniors-card-title">Digital Literacy Program</h2>
                <p className="about-text seniors-card-text">
                  Choicest Fruit<span className="small-r-symbol">®</span> provides hands-on instruction that helps seniors
                  confidently use smartphones, computers, and the internet.
                </p>
                <ul className="seniors-bullets">
                  <li>Practical, beginner-friendly lessons</li>
                  <li>Safe online habits and cybersecurity basics</li>
                  <li>Supportive learning environment</li>
                </ul>
                <Button onClick={handleRegister} className="custom-yellow-btn seniors-cta-btn seniors-cta-btn-secondary">
                  Register Here
                </Button>
              </div>

              <div className="policy-card seniors-image-card" aria-label="Seniors training portrait">
                <Tilt glareEnable={false}>
                  <img src={seniorsTraining} className="img-fluid seniors-image" alt="Seniors technology training" />
                </Tilt>
              </div>
            </Col>
          </Row>
        </div>

        <section
          ref={overviewRef}
          className={`seniors-section seniors-reveal${overviewSeen ? " is-seen" : ""}`}
          aria-label="Skills and topics"
        >
          <Row>
            <Col md={12}>
              <div className="policy-card seniors-copy-card">
                <h2 className="seniors-section-heading">Skills We Teach</h2>
                <p className="seniors-body">
                  Our Digital Literacy Program for Seniors equips participants with practical skills to thrive in a
                  technology-driven world:
                </p>
                <ul className="seniors-list">
                  {skillsTaught.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={whyRef}
          className={`seniors-section seniors-reveal${whySeen ? " is-seen" : ""}`}
          aria-label="Why digital literacy matters"
        >
          <Row>
            <Col md={12}>
              <div className="policy-card seniors-copy-card">
                <h2 className="seniors-section-heading">Why Digital Literacy Matters</h2>
                <p className="seniors-body">Digital skills enable seniors to:</p>
                <ul className="seniors-list">
                  {whyItMatters.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <h3 className="seniors-subheading">Our Mission</h3>
                <p className="seniors-body">
                  This program bridges the digital divide and strengthens independence. Seniors unlock opportunities for
                  connection, personal growth, and active community engagement—leading to a more fulfilling, confident, and
                  connected life.
                </p>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={approachRef}
          className={`seniors-section seniors-reveal${approachSeen ? " is-seen" : ""}`}
          aria-label="Our approach"
        >
          <h2 className="seniors-section-heading seniors-section-heading-center">Our Approach</h2>
          <div className="seniors-grid">
            {approach.map((item) => (
              <div key={item.title} className="policy-card seniors-feature-card">
                <h3 className="seniors-feature-title">{item.title}</h3>
                <p className="seniors-feature-text">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          ref={videoRef}
          className={`seniors-section seniors-reveal${videoSeen ? " is-seen" : ""}`}
          aria-label="Program video"
        >
          <h2 className="seniors-section-heading seniors-section-heading-center">Program Video</h2>
          <div className="policy-card seniors-video-card">
            <div className="seniors-video">
              <iframe
                title="Choicest Fruit program overview"
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section
          ref={testimonialsRef}
          className={`seniors-testimonials-section seniors-reveal${testimonialsSeen ? " is-seen" : ""}`}
          aria-label="Testimonials"
        >
          <h2 className="seniors-section-heading seniors-section-heading-center">Testimonials</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>
      </Container>
    </Container>
  );
}

export default Senior;
